import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Globe, Check, Loader2 } from "lucide-react";

export default function LanguagePicker({ onDone }) {
  const { refresh, realUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [langs, setLangs] = useState([]);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const current = realUser?.language || "en";

  useEffect(() => {
    if (!open) return;
    api.get("/i18n/languages").then(({ data }) => setLangs(data.languages));
    api.get("/i18n/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, [open]);

  const pick = async (code) => {
    if (busy || code === current) { setOpen(false); return; }
    if (status && status.free_changes_left <= 0) {
      if (!window.confirm(`You've used your free language changes — this change costs ${status.cost} BLC. Continue?`)) return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/i18n/set-language", { lang: code });
      localStorage.setItem("pb_lang", code);
      toast.success(data.charged ? `Language changed (${data.charged} BLC)` : "Language changed");
      await refresh();
      setOpen(false);
      onDone?.();
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't change language");
    }
    setBusy(false);
  };

  return (
    <div data-no-translate>
      <button data-testid="language-picker-btn" onClick={() => setOpen((o) => !o)} className="hm-item w-full">
        <Globe size={22} className="text-pb-cyan" />
        <span>Language{current !== "en" ? ` · ${current.toUpperCase()}` : ""}</span>
      </button>
      {open && (
        <div className="border-t border-pb-border/50 mt-1 pt-1 max-h-56 overflow-y-auto" data-testid="language-picker-list">
          {status && (
            <div className="px-3 py-1 text-[9px] uppercase tracking-widest text-zinc-500">
              {status.free_changes_left > 0 ? `${status.free_changes_left} free change${status.free_changes_left !== 1 ? "s" : ""} left` : `Changes cost ${status.cost} BLC`}
            </div>
          )}
          {langs.map((l) => (
            <button key={l.code} data-testid={`lang-${l.code}`} disabled={busy} onClick={() => pick(l.code)}
              className={`w-full flex items-center justify-between px-3 py-2 text-xs transition-colors ${l.code === current ? "text-pb-cyan bg-white/5" : "text-zinc-300 hover:bg-white/5"}`}>
              {l.label}
              {l.code === current && <Check className="w-3.5 h-3.5" />}
            </button>
          ))}
          {busy && <div className="px-3 py-2"><Loader2 className="w-4 h-4 animate-spin text-pb-cyan" /></div>}
        </div>
      )}
    </div>
  );
}
